import { useState, useMemo } from 'react';
import {
    Box,
    Button,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { nFormatter } from '../../../Helpers';

const RoundedBar = (props) => {
    const { x, y, width, height, fill } = props;
    const radius = 3;
    return (
        <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill={fill}
            rx={radius}
            ry={radius}
        />
    );
};

export default function SalesChart({ chartData,handleBarClickWrapper,selectedMonth }) {
    const [activeTab, setActiveTab] = useState('all');
    const theme = useTheme();

    const tabLabels = {
        all: 'All',
        services: 'Top Services',
        parts: 'Top Parts',
        packages: 'Top Packages',
    };

    const barColors = {
        services: '#1e72b8',
        parts: '#FB8C00',
        packages: '#6fbd43',
    };

const transformedData = useMemo(() => {
    if (!chartData?.xAxis?.categories || !chartData?.series) return [];

    const categories = chartData.xAxis.categories;
    const servicesData = chartData.series.find(s => s.name?.toLowerCase() === 'services')?.data || [];
    const partsData = chartData.series.find(s => s.name?.toLowerCase() === 'parts')?.data || [];
    const packagesData = chartData.series.find(s => s.name?.toLowerCase() === 'packages')?.data || [];

    return categories.map((month, i) => ({
        month:month||selectedMonth,
        services: servicesData[i] ?? 0,
        parts: partsData[i] ?? 0,
        packages: packagesData[i] ?? 0,
    }));
}, [chartData]);


    return (
        <Box
            sx={{
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 0,
            }}
        >
            <Typography variant="h6" fontWeight={600} mb={2}>
                Historical Sales Performance
            </Typography>

            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Stack direction="row" spacing={1}>
                    {Object.keys(tabLabels).map((key) => (
                        <Button
                            key={key}
                            variant={activeTab === key ? 'outlined' : 'contained'}
                            color={activeTab === key ? 'success' : 'primary'}
                            onClick={() => setActiveTab(key)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: activeTab === key ? 'bold' : 'normal',
                                bgcolor: activeTab === key ? 'background.paper' : 'primary.main',
                                color: activeTab === key ? 'success.main' : '#fff',
                                borderColor: activeTab === key ? 'success.main' : 'transparent',
                                '&:hover': {
                                    bgcolor: activeTab === key ? '#f0f0f0' : 'primary.dark',
                                },
                            }}
                        >
                            {tabLabels[key]}
                        </Button>
                    ))}
                </Stack>
            </Box>

            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={transformedData} barCategoryGap={1}>
                    <XAxis
                        dataKey="month"
                        tick={({ x, y, payload }) => (
                            <text
                                x={x}
                                y={y + 15}
                                textAnchor="middle"
                                fill={theme.palette.text.primary}
                                fontWeight={600}
                                fontSize={12}
                            >
                                {payload.value}
                            </text>
                        )}
                    />
                    <YAxis
                        tickFormatter={(value) => `${nFormatter(value,2)}`}
                        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                    />
                    <Tooltip formatter={(value, name) => [
    `${nFormatter(value,2)} SAR`,
    name.charAt(0).toUpperCase() + name.slice(1) 
  ]}/>
                    <Legend />

                    {activeTab === 'all' ? (
                        <>
                            <Bar
                                dataKey="services"
                                stackId="a"
                                fill={barColors.services}
                                shape={<RoundedBar />}
                                onClick={handleBarClickWrapper}
                            />
                            <Bar
                                dataKey="parts"
                                stackId="a"
                                fill={barColors.parts}
                                shape={<RoundedBar />}
                                onClick={handleBarClickWrapper}
                            />
                            <Bar
                                dataKey="packages"
                                stackId="a"
                                fill={barColors.packages}
                                shape={<RoundedBar />}
                                onClick={handleBarClickWrapper}
                            />
                        </>
                    ) : (
                        <Bar
                            dataKey={activeTab}
                            fill={barColors[activeTab]}
                            shape={<RoundedBar />}
                            onClick={handleBarClickWrapper}
                        />
                    )}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}
